import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { formatPeriodComparison, type MetricPeriodComparison } from '../lib/metricPeriodComparison';
import {
  fetchCallReportSnapshot,
  type CallReportSnapshot,
} from '../lib/callReports';
import type { OnlineBookingVolumePeriod } from '../lib/onlineBookingVolume';

function emptySnapshot(label: string): CallReportSnapshot {
  return { count: 0, previousCount: 0, label };
}

export function useCallReports(period: OnlineBookingVolumePeriod = 'this_month') {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || undefined;
  const [phoneCalls, setPhoneCalls] = useState<CallReportSnapshot>(
    emptySnapshot('Incoming Phone Calls'),
  );
  const [missedCalls, setMissedCalls] = useState<CallReportSnapshot>(
    emptySnapshot('Missed Calls'),
  );
  const [answeredCalls, setAnsweredCalls] = useState<CallReportSnapshot>(
    emptySnapshot('Answered Calls'),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!businessId) {
      setPhoneCalls(emptySnapshot('Incoming Phone Calls'));
      setMissedCalls(emptySnapshot('Missed Calls'));
      setAnsweredCalls(emptySnapshot('Answered Calls'));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [phone, missed, answered] = await Promise.all([
        fetchCallReportSnapshot(businessId, 'phone_calls', period, timeZone),
        fetchCallReportSnapshot(businessId, 'missed_calls', period, timeZone),
        fetchCallReportSnapshot(businessId, 'answered_calls', period, timeZone),
      ]);
      setPhoneCalls(phone);
      setMissedCalls(missed);
      setAnsweredCalls(answered);
    } finally {
      setLoading(false);
    }
  }, [businessId, period, timeZone]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const previousLabel = period === 'last_30_days' ? 'prior 30 days' : 'last month';

  const phoneComparison: MetricPeriodComparison = formatPeriodComparison(
    phoneCalls.count,
    phoneCalls.previousCount,
    previousLabel,
  );
  const missedComparison: MetricPeriodComparison = formatPeriodComparison(
    missedCalls.count,
    missedCalls.previousCount,
    previousLabel,
  );
  const answeredComparison: MetricPeriodComparison = formatPeriodComparison(
    answeredCalls.count,
    answeredCalls.previousCount,
    previousLabel,
  );

  return {
    phoneCalls,
    missedCalls,
    answeredCalls,
    phoneComparison,
    missedComparison,
    answeredComparison,
    loading,
    refresh,
    businessId,
  };
}
